import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";

function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function DataPage() {
  const { has } = useAuth();
  const canExport = has("export_data");
  const canViewImports = has("import_data");
  const [batches, setBatches] = useState([]);

  const loadBatches = async () => {
    if (!canViewImports) return;
    try { const { data } = await api.get("/import/batches"); setBatches(data); } catch { /* */ }
  };
  useEffect(() => { loadBatches(); /* eslint-disable-next-line */ }, []);

  const exportParts = async () => {
    try { const { data } = await api.get("/export/parts", { responseType: "blob" }); downloadBlob(data, "parts_export.xlsx"); }
    catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="data-page">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Data Export</h1>
      <p className="text-sm text-slate-500 mb-5">Export the parts catalog to Excel. <span className="text-slate-400">Bulk CSV/Excel import has been retired — parts are now created by scanning a tag into a lot (donor, standard, or legacy).</span></p>

      <div className="border border-slate-200 bg-white p-4 space-y-3 max-w-md">
        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5" />Export parts</div>
        {canExport ? (
          <Button data-testid="export-parts" variant="outline" className="rounded-lg border-slate-200" onClick={exportParts}><Download className="w-4 h-4 mr-1.5" />Export all parts (.xlsx)</Button>
        ) : <div className="text-xs text-slate-400 font-mono">Your role cannot export data.</div>}
        <p className="text-[11px] text-slate-400">Cost columns are included only if your role can view acquisition cost. Every export carries an <span className="font-semibold">"about this export"</span> sheet that states in words whether the file is COMPLETE, with the row count beside the catalog total — so a short file can never pass as the whole catalog.</p>
      </div>

      {canViewImports && batches.length > 0 && (
        <div className="border border-slate-200 bg-white mt-5">
          <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Import history (read-only · bulk import retired)</div>
          {batches.map((b) => (
            <div key={b.batch_id} data-testid={`batch-${b.batch_id}`} className="flex items-center gap-2 px-3 py-2 border-t border-slate-100 text-sm">
              <span className="font-mono text-xs font-bold">{b.batch_id}</span>
              <span className="flex-1 text-slate-500 font-mono text-[11px]">{b.filename} · {b.count} parts</span>
              <Badge className={`rounded-lg text-[9px] uppercase font-mono ${b.status === "rolled_back" ? "bg-slate-400" : "bg-emerald-600"} text-white`}>{b.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
